<!--
  FrameX Frames list: sorts/groups the working-set frames via markers.js and
  drives selection straight through the `selectedLocations` store (kept in sync
  with the canvas). `decoratedFrames` attaches each item's `mark` so the
  sort/group helpers have a field to work on. See wiki: framex (Right panel).
-->
<script>
  import { sortFrames, nextSortMode, groupFrames, visibleFrameOrder, rangeBetween } from "../markers.js";
  import { plural } from "../labels.js";
  import { selectAllIds, invertSelection } from "../selection.js";
  import {
    collapsedFrameGroups,
    frameGroupKey,
    toggleFrameGroup,
    setFrameGroupsCollapsed,
  } from "../panel-state.js";
  import { selectedLocations } from "#src/stores/ui.js";
  import SortIcon from "@local/assets/icons/sort.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import FrameMarkerRow from "./FrameMarkerRow.svelte";
  import OrphanFramesSection from "./OrphanFramesSection.svelte";

  export let group;
  /** Every frame location in the working set (`allFrames` from FrameXPanel). */
  export let frames;
  /** `{ pageId -> page }`, for the sheet number half of a row's page label. */
  export let pages;
  /** `{ pageId -> zero-based visible index }`, for the "Page P" half. */
  export let visibleIndices;
  /** "Not in FrameX" placements (`framesNotInFrameX`) — frame locations on
      sheets not in the working set. Rendered by OrphanFramesSection below. */
  export let orphanFrames = [];
  /** Bound up to Document.svelte's canvas hover-highlight map. */
  export let hoveredLocations = {};

  function hoverRow(e) {
    hoveredLocations = { [e.detail.loc.id]: true };
  }
  function unhoverRow() {
    hoveredLocations = {};
  }

  const SORT_LABELS = {
    "mark-asc": "Mark ↑",
    "mark-desc": "Mark ↓",
    doc: "Document order",
  };

  const GROUP_OPTIONS = [
    { value: "none", label: "Nothing" },
    { value: "page", label: "Page" },
    { value: "scope", label: "Scope" },
  ];

  // Bindable so RightPanelTabs can read `groupBy` to conditionally render
  // ScopeTotals without duplicating grouping state.
  export let sortMode = "mark-asc";
  export let groupBy = "none";
  // The last row clicked plain or cmd/ctrl — the fixed end a shift-click
  // range is measured from. Deliberately not reset by shift-clicks, so
  // repeated shift-clicks keep extending from the same anchor.
  let anchor = null;
  // The header equivalent of `anchor`: the group a shift-click on a header
  // measures its span from. Separate from the row anchor because the two range
  // over different things (group keys vs location ids).
  let groupAnchor = null;

  // The sheet's own `page.label`, falling back to its page-order number.
  function pageLabelOf(loc) {
    const page = pages?.[loc.page_id];
    const sequence = (visibleIndices?.[loc.page_id] ?? 0) + 1;
    return page?.label || `Page ${sequence}`;
  }

  $: decoratedFrames = (frames || []).map((loc) => ({ ...loc, mark: $group.items[loc.record_id]?.mark }));
  $: groups = groupFrames(sortFrames(decoratedFrames, sortMode), groupBy, {
    scopeOf: (loc) => $group.items[loc.record_id]?.scope,
    pageLabelOf,
  });
  // Only grouped modes draw a header, so only they can be collapsed — the
  // ungrouped "all" bucket has no toggle and must never be hideable.
  $: renderGroups = groups.map((groupEntry) => {
    const selectedCount = groupEntry.frames.reduce(
      (n, loc) => ($selectedLocations[loc.id] ? n + 1 : n),
      0,
    );
    return {
      ...groupEntry,
      collapsed:
        groupBy !== "none" && Boolean($collapsedFrameGroups[frameGroupKey(groupBy, groupEntry.key)]),
      selectedCount,
      allSelected: selectedCount > 0 && selectedCount === groupEntry.frames.length,
      someSelected: selectedCount > 0 && selectedCount < groupEntry.frames.length,
    };
  });
  // Select all / Invert act on the whole list — collapsing a group hides it,
  // it doesn't remove those frames from the working set.
  $: allIds = visibleFrameOrder(groups).map((loc) => loc.id);
  // A shift-range, though, must never reach into rows that aren't on screen.
  $: visibleIds = visibleFrameOrder(renderGroups.filter((g) => !g.collapsed)).map((loc) => loc.id);
  $: allCollapsed = renderGroups.length > 0 && renderGroups.every((g) => g.collapsed);

  function cycleSort() {
    sortMode = nextSortMode(sortMode);
  }

  // All three replace the shared selection, so they drop orphan rows too.
  function selectAll() {
    selectedLocations.set(selectAllIds(allIds));
    anchor = visibleIds[visibleIds.length - 1] ?? null;
    groupAnchor = null;
  }

  function invert() {
    selectedLocations.set(invertSelection(allIds, $selectedLocations));
    anchor = null;
    groupAnchor = null;
  }

  function clearSelection() {
    selectedLocations.set({});
    anchor = null;
    groupAnchor = null;
  }

  function selectRow(event) {
    const { loc, ctrlKey, metaKey, shiftKey } = event.detail;

    if (shiftKey && anchor != null) {
      selectedLocations.set(selectAllIds(rangeBetween(visibleIds, anchor, loc.id)));
      return;
    }

    if (ctrlKey || metaKey) {
      selectedLocations.toggle(loc.id);
    } else {
      selectedLocations.selectOnly(loc.id);
    }
    anchor = loc.id;
    groupAnchor = null;
  }

  const idsOf = (groupEntry) => groupEntry.frames.map((loc) => loc.id);

  // The row anchor must be on screen, or a following shift-click measures from
  // an id rangeBetween can't find in visibleIds and returns an empty range.
  function lastVisibleId(ids) {
    const onScreen = new Set(visibleIds);
    for (let i = ids.length - 1; i >= 0; i -= 1) {
      if (onScreen.has(ids[i])) return ids[i];
    }
    return null;
  }

  // Header clicks mirror the row modifiers: plain replaces (and re-clicking a
  // group that is the whole selection clears it), cmd/ctrl adds or removes,
  // shift spans from groupAnchor.
  function selectGroup(groupEntry, event) {
    const { ctrlKey, metaKey, shiftKey } = event || {};
    const ids = idsOf(groupEntry);

    if (shiftKey && groupAnchor != null) {
      const from = renderGroups.findIndex((g) => g.key === groupAnchor);
      const to = renderGroups.findIndex((g) => g.key === groupEntry.key);
      if (from !== -1 && to !== -1) {
        const span = renderGroups.slice(Math.min(from, to), Math.max(from, to) + 1);
        const spanIds = span.flatMap(idsOf);
        selectedLocations.set(selectAllIds(spanIds));
        // groupAnchor deliberately stays put, so repeated shift-clicks keep
        // extending from the same fixed end — the same rule as the row range.
        anchor = lastVisibleId(spanIds);
        return;
      }
    }

    if (ctrlKey || metaKey) {
      const removing = groupEntry.allSelected;
      const next = { ...$selectedLocations };
      for (const id of ids) {
        if (removing) delete next[id];
        else next[id] = true;
      }
      selectedLocations.set(next);
      groupAnchor = groupEntry.key;
      anchor = removing ? null : lastVisibleId(ids);
      return;
    }

    // Plain click: if this group is already the entire selection, a second
    // click clears it; otherwise it becomes the selection.
    const isWholeSelection =
      groupEntry.allSelected && Object.keys($selectedLocations).length === ids.length;
    if (isWholeSelection) {
      selectedLocations.set({});
      groupAnchor = null;
      anchor = null;
      return;
    }
    selectedLocations.set(selectAllIds(ids));
    groupAnchor = groupEntry.key;
    anchor = lastVisibleId(ids);
  }

  function toggleAllGroups() {
    setFrameGroupsCollapsed(
      groupBy,
      renderGroups.map((g) => g.key),
      !allCollapsed,
    );
  }
</script>

<div class="fx-markers-list">
  <div class="fx-markers-groupby">
    <div class="fx-markers-groupby-select">
      <SelectInput
        label="Group by"
        labelWidth="4rem"
        value={groupBy}
        options={GROUP_OPTIONS}
        on:input={(e) => (groupBy = e.detail.value)} />
    </div>
    <div class="fx-markers-headright">
      <button type="button" class="fx-markers-sort" title={SORT_LABELS[sortMode]} on:click={cycleSort}>
        <SortIcon />
      </button>
      <span class="fx-markers-qty-head">Qty</span>
    </div>
  </div>

  <div class="fx-markers-rule" />

  <div class="fx-markers-header">
    <div class="fx-markers-actions">
      <button type="button" class="fx-text-link" on:click={selectAll}>Select all</button>
      <button type="button" class="fx-text-link" on:click={invert}>Invert</button>
      <button type="button" class="fx-text-link" on:click={clearSelection}>Clear</button>
    </div>
    {#if groupBy !== "none"}
      <button type="button" class="fx-text-link" on:click={toggleAllGroups}>
        {allCollapsed ? "Expand all" : "Collapse all"}
      </button>
    {/if}
  </div>

  <div class="fx-markers-body">
    {#each renderGroups as groupEntry (groupEntry.key)}
      {#if groupBy !== "none"}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="fx-markers-group-header"
          class:all-selected={groupEntry.allSelected}
          class:some-selected={groupEntry.someSelected}
          title="Select every frame in this group — Ctrl/Cmd to add, Shift to extend"
          on:click={(e) => selectGroup(groupEntry, e)}>
          <button
            type="button"
            class="fx-markers-group-toggle"
            class:open={!groupEntry.collapsed}
            aria-expanded={!groupEntry.collapsed}
            title={groupEntry.collapsed ? "Expand group" : "Collapse group"}
            on:click|stopPropagation={() => toggleFrameGroup(groupBy, groupEntry.key)}>
            <CaretDownIcon />
          </button>
          <span class="fx-markers-group-title">{groupEntry.title}</span>
          <span class="fx-markers-group-count">
            {#if groupEntry.allSelected}
              All {groupEntry.frames.length} selected
            {:else if groupEntry.selectedCount > 0}
              {groupEntry.selectedCount} of {groupEntry.frames.length} selected
            {:else}
              {groupEntry.frames.length} frame{plural(groupEntry.frames.length)}
            {/if}
          </span>
        </div>
      {/if}
      {#if !groupEntry.collapsed}
        {#each groupEntry.frames as loc (loc.id)}
          <FrameMarkerRow
            {loc}
            item={$group.items[loc.record_id]}
            settings={$group.data.settings}
            showScope={groupBy === "none"}
            selected={Boolean($selectedLocations[loc.id])}
            on:select={selectRow}
            on:zoom-to-page
            on:hover={hoverRow}
            on:unhover={unhoverRow} />
        {/each}
      {/if}
    {/each}

    <!-- The "Not in FrameX" section trails the working frames, grouped by
         sheet, in every grouping mode. -->
    <OrphanFramesSection
      frames={orphanFrames}
      {group}
      {pages}
      {visibleIndices}
      settings={$group.data.settings}
      on:zoom-to-page
      on:add-sheet
      on:hover={hoverRow}
      on:unhover={unhoverRow} />
  </div>
</div>

<style lang="scss">
  .fx-markers-list {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    user-select: none;
  }

  // Shift-range selection must not paint row labels; inputs inside
  // (the group-by select, any text field) stay normally selectable.
  .fx-markers-list :global(input),
  .fx-markers-list :global(textarea),
  .fx-markers-list :global([contenteditable="true"]) {
    user-select: text;
  }

  .fx-markers-header {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    // No bottom padding on purpose: the first item in the list body brings its
    // own top padding (5px for a group header, 4px for a row), so adding 6px
    // here double-counted the gap and made it 10-11px against 6px above.
    padding: 6px 16px 0;
  }

  // Right cluster of the header: the Mark-sort arrow + the "Qty" column
  // label, mirroring the legacy list's sort icon + "#" heading.
  .fx-markers-headright {
    display: flex;
    flex: none;
    align-items: center;
    gap: 6px;
    color: var(--fx-neutral);
  }

  .fx-markers-sort {
    all: unset;
    display: inline-flex;
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    color: inherit;

    &:hover {
      background: #f0f1f3;
    }

    :global(svg) {
      display: block;
      width: 14px;
      height: 14px;
    }
  }

  // Column heading over each row's quantity; right-aligned to share the
  // rows' right edge so the numbers line up beneath it.
  .fx-markers-qty-head {
    min-width: 2.25rem;
    text-align: right;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--fx-neutral);
  }

  // The legacy list's line break below the section heading, inset to match
  // the header/row padding.
  .fx-markers-rule {
    height: 0;
    border-top: 1px solid var(--fx-border-light);
    margin: 0 16px;
  }

  // Select all / Invert / Clear, on the row below the rule alongside
  // Collapse all — bulk actions sit with the list they act on.
  .fx-markers-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .fx-markers-groupby {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 16px;
  }

  // SelectInput's root is `@apply w-full`, so it needs an explicitly
  // shrinkable box or it pushes the Collapse all link off the row.
  .fx-markers-groupby-select {
    flex: 1 1 auto;
    min-width: 0;
  }

  .fx-markers-header > button {
    flex: none;
    white-space: nowrap;
  }

  .fx-markers-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .fx-markers-group-header {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 5px 16px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 700;
    color: var(--fx-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    // Gray in every state, so a header always reads as chrome rather than as
    // another row — it used to take the row-selection fill and became one.
    background: var(--fx-border-light);
    // The rule between the previous group's last frame and this header. A border
    // rather than another inset shadow, which the selection bar already owns.
    border-top: 1px solid var(--fx-border);

    // Nothing sits above the first header, so it needs no divider.
    &:first-child {
      border-top: none;
    }

    // A step darker, not lighter — the old #f7f8fa was lighter than the new rest
    // state, so hovering would have washed the header out.
    &:hover {
      background: var(--fx-border);
    }

    // Fully selected gets its own fill plus the edge bar. Deliberately NOT
    // --fx-accent-tint: that is the row-selection field, and borrowing it is what
    // made the header read as another row. --fx-accent-strip-border is a step
    // deeper, so it reads as selected while staying obviously a different object.
    // The text darkens with it, since --fx-text-secondary on that fill is too weak.
    //
    // Declared after the base :hover, which it ties with on specificity — so a
    // hovered selected header keeps this fill instead of reverting to gray.
    &.all-selected {
      background: var(--fx-accent-strip-border);
      color: var(--fx-accent-hover);
      box-shadow: inset 3px 0 0 var(--fx-accent);
    }

    // Partial stays on the gray with only a pale bar. The fill is reserved for
    // "all of this is selected", so the two states can't be confused — and there is
    // no tone between the gray and the selected fill that would read as "some".
    &.some-selected {
      box-shadow: inset 3px 0 0 var(--fx-accent-strip-border);
    }
  }

  // Mirrors .fx-orphans-chevron so the two collapsible headers in this
  // scrolling body read as the same control. `display: inline-flex` because
  // `all: unset` would otherwise leave it inline, where transform is a no-op.
  // Padding widens the hit target and the matching negative margin cancels it,
  // keeping the title on the same x-offset as the "Not in FrameX" title below.
  .fx-markers-group-toggle {
    all: unset;
    display: inline-flex;
    flex: none;
    align-self: center;
    padding: 3px;
    margin: -3px;
    cursor: pointer;
    color: inherit;
    // The caret ships pointing down — that's the expanded state — so it's the
    // collapsed state that carries the rotation.
    transform: rotate(-90deg);
    transition: transform 0.12s ease;

    &.open {
      transform: rotate(0deg);
    }

    :global(svg) {
      display: block;
      width: 12px;
      height: 12px;
    }
  }

  .fx-markers-group-count {
    font-weight: 400;
    text-transform: none;
    letter-spacing: normal;
  }
</style>
